import { createFileRoute, Link, useSearch, useNavigate } from "@tanstack/react-router";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { ArrowLeft, Clock } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { Stripe } from "@stripe/stripe-js";
import { getStripe } from "@/lib/stripe";
import { TenantThemeProvider, PoweredByZyntra } from "@/components/TenantThemeProvider";
import { getBrandingForEvent, getDefaultBranding, type TenantBranding } from "@/lib/branding.functions";
import { supabase } from "@/integrations/supabase/client";

type CheckoutSearch = { secret?: string; eventId?: string };

// Holds are created with a 10-minute TTL in create-ticket-checkout.
const HOLD_DURATION_SECONDS = 10 * 60;

export const Route = createFileRoute("/checkout/$sessionId")({
  component: CheckoutPage,
  validateSearch: (search: Record<string, unknown>): CheckoutSearch => ({
    secret: typeof search.secret === "string" ? search.secret : undefined,
    eventId: typeof search.eventId === "string" ? search.eventId : undefined,
  }),
  errorComponent: ({ error }) => (
    <div className="mx-auto max-w-2xl px-4 pt-10 text-center">
      <h1 className="text-2xl font-bold text-foreground">Error en el checkout</h1>
      <p className="mt-2 text-sm text-muted-foreground break-words">{error.message}</p>
      <Link to="/" className="mt-4 inline-block text-primary hover:underline">Volver al inicio</Link>
    </div>
  ),
});

function CheckoutPage() {
  const { secret, eventId } = useSearch({ from: "/checkout/$sessionId" });
  const navigate = useNavigate();
  const completedRef = useRef(false);
  const [secondsLeft, setSecondsLeft] = useState(HOLD_DURATION_SECONDS);
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [branding, setBranding] = useState<TenantBranding | null>(null);
  const [checkoutMessage, setCheckoutMessage] = useState<string | null>(null);

  // Load tenant branding (by event when available, else default)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const b = eventId ? await getBrandingForEvent(eventId) : await getDefaultBranding();
        if (!cancelled) setBranding(b);
        if (b?.tenant_id) {
          const { data } = await supabase
            .from("tenant_settings")
            .select("checkout_message")
            .eq("tenant_id", b.tenant_id)
            .maybeSingle();
          if (!cancelled) setCheckoutMessage(data?.checkout_message ?? null);
        }
      } catch (e) {
        console.error("[checkout] branding load failed", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  // Memoize the Stripe promise so it isn't recreated on every render
  const stripePromise = useMemo<Promise<Stripe | null> | null>(() => {
    try {
      return getStripe();
    } catch (e) {
      console.error("[checkout] getStripe failed:", e);
      setStripeError((e as Error).message || "No se pudo inicializar el procesador de pagos");
      return null;
    }
  }, []);

  useEffect(() => {
    const startedAt = Date.now();
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startedAt) / 1000);
      const remaining = Math.max(HOLD_DURATION_SECONDS - elapsed, 0);
      setSecondsLeft(remaining);
      if (remaining <= 0) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleCancel = (e: React.MouseEvent) => {
    e.preventDefault();
    // Holds expire automatically via the expire_old_holds cron job.
    navigate({ to: "/" });
  };

  if (!secret) {
    return (
      <TenantThemeProvider branding={branding}>
        <div className="mx-auto max-w-2xl px-4 pt-10 text-center">
          <h1 className="text-2xl font-bold text-foreground">Sesión inválida</h1>
          <p className="mt-2 text-muted-foreground">No se pudo iniciar el checkout.</p>
          <Link to="/" className="mt-4 inline-block text-primary hover:underline">Volver al inicio</Link>
        </div>
      </TenantThemeProvider>
    );
  }

  const expired = secondsLeft <= 0;
  const warning = secondsLeft > 0 && secondsLeft <= 60;
  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  const formatted = `${minutes}:${seconds.toString().padStart(2, "0")}`;
  const isCustomTenant = !!branding && branding.tenant_slug !== "zyntra";

  return (
    <TenantThemeProvider branding={branding}>
      <div className="mx-auto max-w-3xl px-4 pb-20 pt-6 sm:px-6">
        <a
          href="/"
          onClick={handleCancel}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors cursor-pointer"
        >
          <ArrowLeft className="h-4 w-4" />
          Cancelar
        </a>

        {/* Tenant brand header — only renders when a custom tenant has assets */}
        {isCustomTenant && (branding?.logo_url || branding?.brand_name) && (
          <div className="mb-4 flex items-center gap-3 rounded-2xl border border-border bg-card/60 p-3">
            {branding?.logo_url ? (
              <img
                src={branding.logo_url}
                alt={branding.brand_name}
                className="h-9 w-9 rounded-lg object-contain bg-background"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
              />
            ) : null}
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">
                {branding?.brand_name}
              </p>
              {branding?.brand_tagline ? (
                <p className="text-xs text-muted-foreground truncate">{branding.brand_tagline}</p>
              ) : null}
            </div>
          </div>
        )}

        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold text-foreground">Pagar entradas</h1>
          <div
            className={`inline-flex items-center gap-2 self-start rounded-full border px-3 py-1.5 text-sm font-medium tabular-nums transition-colors ${
              expired
                ? "border-destructive/40 bg-destructive/10 text-destructive"
                : warning
                  ? "border-warning/40 bg-warning/10 text-warning"
                  : "border-border bg-secondary text-secondary-foreground"
            }`}
            aria-live="polite"
          >
            <Clock className="h-4 w-4" />
            {expired ? (
              <span>Reservas expiradas</span>
            ) : (
              <span>
                Reservas: <span className="font-semibold">{formatted}</span>
              </span>
            )}
          </div>
        </div>

        {checkoutMessage && !expired ? (
          <div className="mb-4 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-foreground">
            {checkoutMessage}
          </div>
        ) : null}

        {expired ? (
          <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6 text-center">
            <p className="text-foreground font-medium">Tus reservas expiraron</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Volvé al evento para iniciar una nueva compra.
            </p>
            <button
              onClick={() => navigate({ to: "/" })}
              className="mt-4 inline-flex items-center justify-center rounded-lg gradient-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-all hover:opacity-90"
            >
              Volver al inicio
            </button>
          </div>
        ) : stripeError || !stripePromise ? (
          <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6 text-center">
            <p className="text-foreground font-medium">No se pudo cargar el formulario de pago</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {stripeError || "El procesador de pagos no está configurado."}
            </p>
            <button
              onClick={() => navigate({ to: "/" })}
              className="mt-4 inline-flex items-center justify-center rounded-lg gradient-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-all hover:opacity-90"
            >
              Volver al inicio
            </button>
          </div>
        ) : (
          <div className="rounded-2xl bg-white p-2 sm:p-4">
            <EmbeddedCheckoutProvider
              stripe={stripePromise}
              options={{
                clientSecret: secret,
                onComplete: () => {
                  completedRef.current = true;
                },
              }}
            >
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </div>
        )}

        <PoweredByZyntra className="mt-6 text-center" />
      </div>
    </TenantThemeProvider>
  );
}
